import { runWithLoading, subscribeWithLoading } from '../../redux/loadingOperation'
import firebase from 'firebase/compat/app'

import TasksHelper from '../../../components/TaskListView/Utils/TasksHelper'
import {
    deleteFolderFilesInStorage,
    getDb,
    getId,
    globalWatcherUnsub,
    logEvent,
    proccessPictureForAvatar,
    runHttpsCallableFunction,
    updateRemovedWorkflowStepSubtaks,
    updateRemovedWorkflowStepTaks,
    uploadAvatarPhotos,
} from '../firestore'
import { BatchWrapper } from '../../../functions/BatchWrapper/batchWrapper'
import store from '../../../redux/store'
import { setAssistantsInProject, setGlobalAssistants } from '../../../redux/actions'
import {
    DEFAULT_EMAIL_SIGNATURE,
    GLOBAL_PROJECT_ID,
    TYPE_3RD_PARTY,
    TYPE_PROMPT_BASED,
    isGlobalAssistant,
} from '../../../components/AdminPanel/Assistants/assistantsHelper'
import {
    assistantDescriptionChangedUpdatesChain,
    assistantInstructionsChangedUpdatesChain,
    assistantModelChangedUpdatesChain,
    assistantNameChangedUpdatesChain,
    assistantPictureChangedUpdatesChain,
    assistantTemperatureChangedUpdatesChain,
    assistantTypeChangedUpdatesChain,
    createAssistantUpdatesChain,
    deleteAssistantUpdatesChain,
    updateAssistantFeedsChain,
} from './assistantUpdates'
import { updateNoteTitleWithoutFeed } from '../Notes/notesFirestore'
import { updateChatTitleWithoutFeeds } from '../Chats/chatsFirestore'
import ProjectHelper from '../../../components/SettingsView/ProjectsSettings/ProjectHelper'
import { RECURRENCE_NEVER, RECURRENCE_ONCE } from '../../../components/TaskListView/Utils/TasksHelper'
import {
    getAssistantPreConfigSearchRows,
    settlePreConfigTaskSearchSources,
    sortPreConfigTaskSearchItems,
} from './preConfigTaskSearchHelper'
import { getPreConfigTaskModelOverride } from '../../../functions/Assistant/preConfigTaskModel'
import { getWorkflowSortIndexUpdates, getWorkflowStepsIdsSorted } from '../../workflowOrder'
import {
    ASSISTANT_WORKFLOW_FIRST_STEP_ID,
    buildAssistantWorkflowFirstStep,
    getAssistantWorkflow,
} from '../../assistantWorkflow'
import { normalizeAssistantReasoningEffort } from '../../../functions/Assistant/selectableAssistantReasoningEfforts'
import { getAssistantTemplateState, getTaskTemplateState } from '../../../functions/Assistants/templateMerge'

const MAX_ASSISTANT_PROMPT_HISTORY = 10
export const ASSISTANT_PROMPT_FIELD_INSTRUCTIONS = 'instructions'
export const ASSISTANT_PROMPT_FIELD_HEARTBEAT = 'heartbeatPrompt'
export const ASSISTANT_PROMPT_HISTORY_FIELD_INSTRUCTIONS = 'instructionsHistory'
export const ASSISTANT_PROMPT_HISTORY_FIELD_HEARTBEAT = 'heartbeatPromptHistory'
const DEFAULT_HEARTBEAT_AWAKE_START = 28800000
const DEFAULT_HEARTBEAT_AWAKE_END = 79200000
const DEFAULT_HEARTBEAT_INTERVAL_MS = 1800000
const DEFAULT_HEARTBEAT_CHANCE_PERCENT = 0
const DEFAULT_HEARTBEAT_CHANCE_NO_REPLY_PERCENT = 0
const DEFAULT_HEARTBEAT_SEND_WHATSAPP = false
export const DEFAULT_HEARTBEAT_PROMPT =
    'Check the done tasks today, comment on it and/or the chat history with one sentence and ask the user if he already did the focus task (remind him) or if there are any other ways you can help.'
const ASSISTANT_TASKS_CACHE_MAX_AGE = 120000
const assistantTasksCache = {}

export const getAssistantTemplateSnapshot = getAssistantTemplateState

function getAssistantTasksCollectionPath(projectId, assistantId) {
    return projectId === GLOBAL_PROJECT_ID || isGlobalAssistant(assistantId)
        ? `assistantTasks/${projectId}/preConfigTasks`
        : `assistantTasks/${projectId}/${assistantId}`
}

const getAssistantTasksCacheKey = (projectId, assistantId) => `${projectId}/${assistantId}`

const sortAssistantTasks = tasks => {
    tasks.sort((a, b) => {
        const typeRank = value => {
            if (!value) return 1
            return value === 'prompt' ? 0 : 1
        }

        const typeDiff = typeRank(a.type) - typeRank(b.type)
        if (typeDiff !== 0) return typeDiff

        return (a.order ?? 0) - (b.order ?? 0)
    })
    return tasks
}

const setAssistantTasksCache = (projectId, assistantId, tasks) => {
    assistantTasksCache[getAssistantTasksCacheKey(projectId, assistantId)] = {
        tasks: tasks.map(task => ({ ...task })),
        updatedAt: Date.now(),
    }
}

const getAssistantTasksCache = (projectId, assistantId) => {
    const cachedData = assistantTasksCache[getAssistantTasksCacheKey(projectId, assistantId)]
    if (!cachedData || Date.now() - cachedData.updatedAt > ASSISTANT_TASKS_CACHE_MAX_AGE) return null
    return cachedData.tasks.map(task => ({ ...task }))
}

const clearAssistantTasksCache = (projectId, assistantId) => {
    delete assistantTasksCache[getAssistantTasksCacheKey(projectId, assistantId)]
}

const mapAssistantTaskData = (id, data) => {
    const task = { ...(data || {}), id }
    task.aiModel = getPreConfigTaskModelOverride(task)
    return task
}

function getAssistantTaskDocRef(projectId, assistantId, taskId) {
    return getDb().doc(`${getAssistantTasksCollectionPath(projectId, assistantId)}/${taskId}`)
}

function getActivatedUserIds(task) {
    const completedOneOffUserIds = new Set(task?.completedOneOffUserIds || [])
    const recurrenceByUser = task?.recurrenceByUser || {}
    const recurrenceUserIds = Object.entries(recurrenceByUser)
        .filter(
            ([userId, recurrence]) =>
                recurrence &&
                recurrence !== RECURRENCE_NEVER &&
                !(recurrence === RECURRENCE_ONCE && completedOneOffUserIds.has(userId))
        )
        .map(([userId]) => userId)

    const ids = Array.isArray(task?.activatedUserIds)
        ? task.activatedUserIds.filter(userId => userId && !completedOneOffUserIds.has(userId))
        : []
    const hasExplicitActivationState = Array.isArray(task?.activatedUserIds) || task?.recurrenceByUser !== undefined
    if (hasExplicitActivationState) return [...new Set([...ids, ...recurrenceUserIds])]

    if (!task?.recurrence || task.recurrence === RECURRENCE_NEVER) return []

    const fallbackIds = [task?.activatorUserId, task?.creatorUserId, task?.userId].filter(
        userId => userId && !completedOneOffUserIds.has(userId)
    )
    return [...new Set(fallbackIds)]
}

//ACCESS FUNCTIONS

export async function getAssistantData(projectId, assistantId) {
    const assistant = (await getDb().doc(`assistants/${projectId}/items/${assistantId}`).get()).data()
    if (assistant) assistant.uid = assistantId
    return assistant
}

export async function getGlobalAssistants() {
    const assistantDocs = (
        await getDb().collection(`assistants/${GLOBAL_PROJECT_ID}/items`).orderBy('lastEditionDate', 'desc').get()
    ).docs
    const assistants = []
    assistantDocs.forEach(doc => {
        const assistant = doc.data()
        assistant.uid = doc.id
        assistants.push(assistant)
    })
    return assistants
}

export function watchAssistants(projectId, watcherKey, callback, { onError } = {}) {
    globalWatcherUnsub[watcherKey] = subscribeWithLoading(
        'project_assistants',
        (next, error) =>
            getDb()
                .collection(`assistants/${projectId}/items`)
                .orderBy('lastEditionDate', 'desc')
                .onSnapshot(next, error),
        assistantDocs => {
            let assistants = []
            assistantDocs.forEach(doc => {
                const assistant = doc.data()
                assistant.uid = doc.id
                assistants.push(assistant)
            })
            callback(assistants)
        },
        { onError }
    )
}

export function watchAssistant(projectId, assistantId, watcherKey, callback) {
    globalWatcherUnsub[watcherKey] = subscribeWithLoading(
        'assistant',
        (next, error) => getDb().doc(`assistants/${projectId}/items/${assistantId}`).onSnapshot(next, error),
        doc => {
            const assistant = doc.data()
            if (assistant) assistant.uid = doc.id

            callback(assistant)
        }
    )
}

export async function getProjectAssistants(projectId) {
    const assistantDocs = (await getDb().collection(`assistants/${projectId}/items`).get()).docs

    const assistants = []
    assistantDocs.forEach(doc => {
        const assistant = doc.data()
        assistant.uid = doc.id
        assistants.push(assistant)
    })

    return assistants
}

export function watchAssistantTasks(projectId, assistantId, watcherKey, callback) {
    const collectionPath = getAssistantTasksCollectionPath(projectId, assistantId)
    let query = getDb().collection(collectionPath)

    if (isGlobalAssistant(assistantId)) {
        query = query.where('assistantId', '==', assistantId)
    }

    globalWatcherUnsub[watcherKey] = subscribeWithLoading(
        'assistant_tasks',
        (next, error) => query.onSnapshot(next, error),
        assistantDocs => {
            const tasks = []
            assistantDocs.forEach(doc => {
                tasks.push(mapAssistantTaskData(doc.id, doc.data()))
            })

            sortAssistantTasks(tasks)
            setAssistantTasksCache(projectId, assistantId, tasks)
            callback(tasks)
        }
    )
}

export async function getPreConfigTasksForProject(projectId) {
    const { projectAssistants, globalAssistants, loggedUser } = store.getState()
    const project = ProjectHelper.getProjectById(projectId)

    const rows = getAssistantPreConfigSearchRows({
        loggedUserProjects: project ? [project] : [],
        projectAssistants,
        globalAssistants,
        loggedUserId: loggedUser?.uid,
    })

    const taskGroups = await Promise.all(
        rows.map(async ({ project, assistant, projectSearchOrder, assistantSearchOrder }) => {
            const tasksProjectId = isGlobalAssistant(assistant.uid) ? GLOBAL_PROJECT_ID : projectId
            const tasks = await getAssistantTasks(tasksProjectId, assistant.uid)
            return tasks.map(task => ({
                ...task,
                id: task.id,
                projectId,
                project,
                assistant, // Attach for display
                isPreConfigTask: true,
                assistantId: assistant.uid,
                projectSearchOrder,
                assistantSearchOrder,
            }))
        })
    )

    return sortPreConfigTaskSearchItems(taskGroups.flat(), loggedUser?.uid)
}

async function getAssistantTasks(projectId, assistantId) {
    const cachedTasks = getAssistantTasksCache(projectId, assistantId)
    if (cachedTasks) return cachedTasks

    const collectionPath = getAssistantTasksCollectionPath(projectId, assistantId)
    let query = getDb().collection(collectionPath)

    if (isGlobalAssistant(assistantId)) {
        query = query.where('assistantId', '==', assistantId)
    }

    const snapshot = await query.get()
    const tasks = []
    snapshot.forEach(doc => {
        tasks.push(mapAssistantTaskData(doc.id, doc.data()))
    })

    sortAssistantTasks(tasks)
    setAssistantTasksCache(projectId, assistantId, tasks)
    return tasks
}

export async function getPreConfigTasksForAllProjects() {
    const { loggedUserProjects, projectAssistants, globalAssistants, loggedUser } = store.getState()
    const rows = getAssistantPreConfigSearchRows({
        loggedUserProjects,
        projectAssistants,
        globalAssistants,
        loggedUserId: loggedUser?.uid,
    })
    const sourcesByCacheKey = {}
    const allTasks = []

    rows.forEach(({ project, assistant, projectSearchOrder, assistantSearchOrder }) => {
        const tasksProjectId = isGlobalAssistant(assistant.uid) ? GLOBAL_PROJECT_ID : project.id
        const cacheKey = getAssistantTasksCacheKey(tasksProjectId, assistant.uid)
        if (!sourcesByCacheKey[cacheKey]) {
            sourcesByCacheKey[cacheKey] = {
                tasksProjectId,
                assistantId: assistant.uid,
                rows: [],
            }
        }
        sourcesByCacheKey[cacheKey].rows.push({ project, assistant, projectSearchOrder, assistantSearchOrder })
    })

    const { loadedSources, failedSources } = await settlePreConfigTaskSearchSources(
        Object.values(sourcesByCacheKey),
        source => getAssistantTasks(source.tasksProjectId, source.assistantId)
    )

    loadedSources.forEach(({ source, tasks }) => {
        source.rows.forEach(({ project, assistant, projectSearchOrder, assistantSearchOrder }) => {
            tasks.forEach(task => {
                allTasks.push({
                    ...task,
                    id: task.id,
                    searchId: `${project.id}_${assistant.uid}_${task.id}`,
                    projectId: project.id,
                    project,
                    assistant,
                    assistantId: assistant.uid,
                    isPreConfigTask: true,
                    projectSearchOrder,
                    assistantSearchOrder,
                })
            })
        })
    })

    if (failedSources.length > 0) {
        const failureDetails = failedSources.map(({ source, error }) => ({
            tasksProjectId: source.tasksProjectId,
            assistantId: source.assistantId,
            code: error?.code || error?.name || 'unknown',
        }))
        console.warn('[Assistant task search] Skipped unavailable task sources:', failureDetails)

        if (allTasks.length === 0) {
            const error = new Error('Assistant tasks could not be loaded')
            error.code = 'assistant-task-search-load-failed'
            error.failedSources = failureDetails
            throw error
        }
    }

    return sortPreConfigTaskSearchItems(allTasks, loggedUser?.uid)
}

export async function getPreConfigTask(projectId, assistantId, taskId) {
    const taskRef = getAssistantTaskDocRef(projectId, assistantId, taskId)
    const doc = await taskRef.get()
    if (doc.exists) {
        return mapAssistantTaskData(doc.id, doc.data())
    }
    return null
}

//EDTION AND ADITION FUNCTIONS

export const updateAssistantEditionData = async (projectId, assistantId, editorId) => {
    await getDb().runTransaction(async transaction => {
        const ref = getDb().doc(`assistants/${projectId}/items/${assistantId}`)
        const doc = await transaction.get(ref)
        if (doc.exists) transaction.update(ref, { lastEditionDate: Date.now(), lastEditorId: editorId })
    })
}

const updateEditionData = data => {
    const { loggedUser } = store.getState()
    data.lastEditionDate = Date.now()
    data.lastEditorId = loggedUser.uid
}

async function updateAssistantData(projectId, assistantId, data, batch) {
    updateEditionData(data)
    const ref = getDb().doc(`assistants/${projectId}/items/${assistantId}`)
    batch ? batch.update(ref, data) : await ref.update(data)
}

export async function ensureAssistantWorkflowFirstStep(projectId, assistant) {
    const workflow = getAssistantWorkflow(assistant, projectId)
    const existingStep = workflow[ASSISTANT_WORKFLOW_FIRST_STEP_ID]
    const orderedStepIds = [
        ASSISTANT_WORKFLOW_FIRST_STEP_ID,
        ...getWorkflowStepsIdsSorted(workflow).filter(stepId => stepId !== ASSISTANT_WORKFLOW_FIRST_STEP_ID),
    ]
    const workflowAlreadyValid =
        existingStep?.reviewerUid === assistant.uid &&
        existingStep?.reviewerType === 'assistant' &&
        orderedStepIds.every((stepId, index) => workflow[stepId]?.sortIndex === index)
    if (workflowAlreadyValid) return existingStep

    const { loggedUser } = store.getState()
    const firstStep = existingStep || buildAssistantWorkflowFirstStep(assistant.uid, loggedUser.uid)
    const updates = existingStep
        ? {
              [`workflow.${projectId}.${ASSISTANT_WORKFLOW_FIRST_STEP_ID}.reviewerUid`]: assistant.uid,
              [`workflow.${projectId}.${ASSISTANT_WORKFLOW_FIRST_STEP_ID}.reviewerType`]: 'assistant',
              [`workflow.${projectId}.${ASSISTANT_WORKFLOW_FIRST_STEP_ID}.sortIndex`]: 0,
          }
        : { [`workflow.${projectId}.${ASSISTANT_WORKFLOW_FIRST_STEP_ID}`]: firstStep }

    orderedStepIds.slice(1).forEach((stepId, index) => {
        updates[`workflow.${projectId}.${stepId}.sortIndex`] = index + 1
    })
    await updateAssistantData(projectId, assistant.uid, updates, null)
    return firstStep
}

export async function addAssistantWorkflowStep(projectId, assistantId, newStepData) {
    const newStepId = getId()
    await updateAssistantData(projectId, assistantId, { [`workflow.${projectId}.${newStepId}`]: newStepData }, null)
    return newStepId
}

export async function reorderAssistantWorkflowSteps(projectId, assistantId, orderedStepIds) {
    const orderedWithLockedFirstStep = [
        ASSISTANT_WORKFLOW_FIRST_STEP_ID,
        ...orderedStepIds.filter(stepId => stepId !== ASSISTANT_WORKFLOW_FIRST_STEP_ID),
    ]
    await updateAssistantData(
        projectId,
        assistantId,
        getWorkflowSortIndexUpdates(projectId, orderedWithLockedFirstStep),
        null
    )
}

const updateAssistantEditedWorkflowStepTasks = (projectId, tasks, stepId, reviewerUid, parentTasksIndices, batch) => {
    tasks.forEach(taskData => {
        const task = taskData.data()
        if (!Array.isArray(task.userIds) || !Array.isArray(task.stepHistory)) return

        const index = task.parentId
            ? parentTasksIndices[task.parentId]
            : task.stepHistory.findIndex(id => id === stepId)
        if (index == null || index < 0 || index >= task.userIds.length) return

        task.projectId = projectId
        task.userIds[index] = reviewerUid
        if (index === task.stepHistory.length - 1) task.currentReviewerId = reviewerUid
        if (task.subtaskIds?.length > 0) parentTasksIndices[taskData.id] = index

        batch.set(getDb().doc(`items/${projectId}/tasks/${taskData.id}`), task)
    })
    return batch
}

export async function modifyAssistantWorkflowStep(projectId, assistantId, stepId, newStepData, oldReviewerUid) {
    const batch = new BatchWrapper(getDb())
    const newStep = { ...newStepData }
    delete newStep.id

    if (stepId === ASSISTANT_WORKFLOW_FIRST_STEP_ID) {
        newStep.reviewerUid = assistantId
        newStep.reviewerType = 'assistant'
        newStep.sortIndex = 0
    }

    updateAssistantData(projectId, assistantId, { [`workflow.${projectId}.${stepId}`]: newStep }, batch)

    if (newStep.reviewerUid !== oldReviewerUid) {
        const parentTasksIndices = {}
        const [tasks, subtasks] = await Promise.all([
            getDb()
                .collection(`items/${projectId}/tasks`)
                .where('userId', '==', assistantId)
                .where('done', '==', false)
                .where('userIds', 'array-contains', oldReviewerUid)
                .where('parentId', '==', null)
                .get(),
            getDb()
                .collection(`items/${projectId}/tasks`)
                .where('userId', '==', assistantId)
                .where('parentDone', '==', false)
                .where('userIds', 'array-contains', oldReviewerUid)
                .where('parentId', '>', '')
                .get(),
        ])

        updateAssistantEditedWorkflowStepTasks(projectId, tasks, stepId, newStep.reviewerUid, parentTasksIndices, batch)
        updateAssistantEditedWorkflowStepTasks(
            projectId,
            subtasks,
            stepId,
            newStep.reviewerUid,
            parentTasksIndices,
            batch
        )
    }

    await batch.commit()
}

export async function removeAssistantWorkflowStep(project, assistantId, stepId, steps, reviewerUid) {
    if (stepId === ASSISTANT_WORKFLOW_FIRST_STEP_ID) return

    let batch = new BatchWrapper(getDb())
    updateAssistantData(
        project.id,
        assistantId,
        { [`workflow.${project.id}.${stepId}`]: firebase.firestore.FieldValue.delete() },
        batch
    )

    const [tasks, subtasks] = await Promise.all([
        getDb()
            .collection(`items/${project.id}/tasks`)
            .where('userId', '==', assistantId)
            .where('done', '==', false)
            .where('userIds', 'array-contains', reviewerUid)
            .where('parentId', '==', null)
            .get(),
        getDb()
            .collection(`items/${project.id}/tasks`)
            .where('userId', '==', assistantId)
            .where('parentDone', '==', false)
            .where('userIds', 'array-contains', reviewerUid)
            .where('parentId', '>', '')
            .get(),
    ])

    const parentTasksIndices = {}
    batch = updateRemovedWorkflowStepTaks(project.id, tasks.docs, steps, stepId, parentTasksIndices, batch)
    batch = updateRemovedWorkflowStepSubtaks(project.id, subtasks.docs, steps, stepId, parentTasksIndices, batch)
    await batch.commit()
}

function getPromptHistoryValue(entry, promptField) {
    if (!entry) return ''
    if (typeof entry[promptField] === 'string') return entry[promptField]
    if (typeof entry.prompt === 'string') return entry.prompt
    if (promptField === ASSISTANT_PROMPT_FIELD_INSTRUCTIONS && typeof entry.instructions === 'string') {
        return entry.instructions
    }
    if (promptField === ASSISTANT_PROMPT_FIELD_HEARTBEAT && typeof entry.heartbeatPrompt === 'string') {
        return entry.heartbeatPrompt
    }
    return ''
}

function buildPromptHistoryEntry(promptField, prompt, replacedAt, replacedByUserId) {
    return {
        prompt,
        [promptField]: prompt,
        replacedAt,
        replacedByUserId: replacedByUserId || null,
        replacedByAssistantId: null,
    }
}

function getCurrentPromptValue(data, promptField) {
    const value = data?.[promptField]
    if (typeof value === 'string') return value
    if (promptField === ASSISTANT_PROMPT_FIELD_HEARTBEAT) return DEFAULT_HEARTBEAT_PROMPT
    return ''
}

async function updateAssistantPromptFieldWithHistory(
    projectId,
    assistant,
    promptField,
    historyField,
    prompt,
    extraData = {}
) {
    const nextPrompt = typeof prompt === 'string' ? prompt : ''
    const { loggedUser } = store.getState()
    const ref = getDb().doc(`assistants/${projectId}/items/${assistant.uid}`)
    const now = Date.now()
    let updated = false

    await getDb().runTransaction(async transaction => {
        const doc = await transaction.get(ref)
        if (!doc.exists) return

        const currentData = doc.data() || {}
        const currentPrompt = getCurrentPromptValue(currentData, promptField)
        if (currentPrompt === nextPrompt) {
            if (Object.keys(extraData).length > 0) {
                transaction.update(ref, {
                    ...extraData,
                    lastEditionDate: now,
                    lastEditorId: loggedUser.uid,
                })
                updated = true
            }
            return
        }

        const existingHistory = Array.isArray(currentData[historyField]) ? currentData[historyField] : []
        const historyEntry = buildPromptHistoryEntry(promptField, currentPrompt, now, loggedUser.uid)
        const nextHistory = [historyEntry, ...existingHistory]
            .filter(entry => getPromptHistoryValue(entry, promptField) !== nextPrompt)
            .slice(0, MAX_ASSISTANT_PROMPT_HISTORY)

        transaction.update(ref, {
            ...extraData,
            [promptField]: nextPrompt,
            [historyField]: nextHistory,
            lastEditionDate: now,
            lastEditorId: loggedUser.uid,
        })
        updated = true
    })

    return updated
}

export async function uploadNewAssistant(projectId, assistant, callback) {
    const { loggedUser } = store.getState()
    updateEditionData(assistant)

    assistant.uid = getId()
    assistant.displayName = assistant.displayName.trim()
    assistant.createdDate = Date.now()
    assistant.creatorId = loggedUser.uid

    const assistantToStore = { ...assistant }
    delete assistantToStore.uid

    const batch = new BatchWrapper(getDb())
    batch.set(getDb().doc(`assistants/${projectId}/items/${assistant.uid}`), assistantToStore, {
        merge: true,
    })

    const cleanedTitle = TasksHelper.getTaskNameWithoutMeta(assistant.displayName)

    await batch.commit()

    if (!isGlobalAssistant(assistant.uid)) createAssistantUpdatesChain(projectId, assistant)
    callback?.(assistant)

    logEvent('new_assistant', {
        uid: assistant.uid,
        name: cleanedTitle,
    })

    return assistant
}

export async function deleteAssistant(projectId, assistant) {
    const batch = new BatchWrapper(getDb())
    batch.delete(getDb().doc(`assistants/${projectId}/items/${assistant.uid}`))

    if (projectId === GLOBAL_PROJECT_ID) {
        const projectDocs = (
            await getDb().collection(`projects`).where('globalAssistantIds', 'array-contains', assistant.uid).get()
        ).docs
        projectDocs.forEach(doc => {
            const project = doc.data()
            const data = { globalAssistantIds: firebase.firestore.FieldValue.arrayRemove(assistant.uid) }
            if (project.assistantId === assistant.uid) data.assistantId = ''
            batch.update(getDb().doc(`projects/${doc.id}`), data)
        })
    } else {
        const project = ProjectHelper.getProjectById(projectId)
        if (project.assistantId === assistant.uid)
            batch.update(getDb().doc(`projects/${projectId}`), { assistantId: '' })
    }

    batch.commit()
    if (!isGlobalAssistant(assistant.uid)) deleteAssistantUpdatesChain(projectId, assistant)
}

export async function updateAssistant(projectId, updatedAssistant, oldAssistant) {
    const assistantId = updatedAssistant.uid

    const assistantToStore = { ...updatedAssistant }
    delete assistantToStore.uid

    const batch = new BatchWrapper(getDb())
    updateAssistantData(projectId, assistantId, assistantToStore, batch)

    if (oldAssistant.displayName !== updatedAssistant.displayName) {
        updateChatTitleWithoutFeeds(projectId, updatedAssistant.uid, updatedAssistant.displayName)
        if (updatedAssistant.noteIdsByProject[projectId])
            await updateNoteTitleWithoutFeed(
                projectId,
                updatedAssistant.noteIdsByProject[projectId],
                updatedAssistant.displayName,
                batch
            )
    }

    batch.commit()

    if (!isGlobalAssistant(updatedAssistant.uid)) updateAssistantFeedsChain(projectId, updatedAssistant, oldAssistant)
}

export async function updateAssistantName(projectId, assistant, newName) {
    const batch = new BatchWrapper(getDb())

    updateAssistantData(projectId, assistant.uid, { displayName: newName }, batch)

    if (assistant.displayName !== newName) {
        updateChatTitleWithoutFeeds(projectId, assistant.uid, newName)
        if (assistant.noteIdsByProject[projectId])
            await updateNoteTitleWithoutFeed(projectId, assistant.noteIdsByProject[projectId], newName, batch)
    }

    batch.commit()

    if (!isGlobalAssistant(assistant.uid))
        assistantNameChangedUpdatesChain(projectId, assistant, assistant.displayName, newName)
}

export function updateAssistantDescription(projectId, description, assistant) {
    updateAssistantData(projectId, assistant.uid, { description }, null)
    if (!isGlobalAssistant(assistant.uid))
        assistantDescriptionChangedUpdatesChain(projectId, assistant, assistant.description, description)
}

export function updateAssistantPrompt(projectId, assistant, prompt) {
    updateAssistantData(projectId, assistant.uid, { type: TYPE_PROMPT_BASED, prompt }, null)
    if (assistant.type !== TYPE_PROMPT_BASED && !isGlobalAssistant(assistant.uid)) {
        assistantTypeChangedUpdatesChain(projectId, assistant, assistant.type, TYPE_PROMPT_BASED)
    }
}

export async function updateAssistantNote(projectId, assistantId, noteId) {
    await updateAssistantData(projectId, assistantId, { [`noteIdsByProject.${projectId}`]: noteId }, null)
}

export function updateAssistantThirdPartLink(projectId, assistant, thirdPartLink) {
    updateAssistantData(projectId, assistant.uid, { type: TYPE_3RD_PARTY, thirdPartLink }, null)
    if (assistant.type !== TYPE_3RD_PARTY && !isGlobalAssistant(assistant.uid)) {
        assistantTypeChangedUpdatesChain(projectId, assistant, assistant.type, TYPE_3RD_PARTY)
    }
}

export async function updateAssistantInstructions(projectId, assistant, instructions) {
    const updated = await updateAssistantPromptFieldWithHistory(
        projectId,
        assistant,
        ASSISTANT_PROMPT_FIELD_INSTRUCTIONS,
        ASSISTANT_PROMPT_HISTORY_FIELD_INSTRUCTIONS,
        instructions
    )
    if (updated && !isGlobalAssistant(assistant.uid))
        assistantInstructionsChangedUpdatesChain(projectId, assistant, assistant.instructions, instructions)
}

export async function updateAssistantHeartbeatSettings(projectId, assistant, data) {
    if (Object.prototype.hasOwnProperty.call(data, ASSISTANT_PROMPT_FIELD_HEARTBEAT)) {
        const { heartbeatPrompt, ...settingsData } = data
        await updateAssistantPromptFieldWithHistory(
            projectId,
            assistant,
            ASSISTANT_PROMPT_FIELD_HEARTBEAT,
            ASSISTANT_PROMPT_HISTORY_FIELD_HEARTBEAT,
            heartbeatPrompt,
            settingsData
        )
    } else {
        await updateAssistantData(projectId, assistant.uid, data, null)
    }
}

export function updateAssistantDelegationDescriptionManual(projectId, assistant, manualText) {
    const delegationToolDescriptionManual = typeof manualText === 'string' ? manualText.trim() : ''
    updateAssistantData(projectId, assistant.uid, { delegationToolDescriptionManual }, null)
}

export function updateAssistantDelegationTargets(projectId, assistant, selectedTargetKeys = [], useDefaultAll = false) {
    if (useDefaultAll) {
        updateAssistantData(
            projectId,
            assistant.uid,
            { allowedDelegationTargetKeys: firebase.firestore.FieldValue.delete() },
            null
        )
        return
    }

    const normalizedTargetKeys = Array.from(
        new Set(
            (Array.isArray(selectedTargetKeys) ? selectedTargetKeys : [])
                .map(key => String(key || '').trim())
                .filter(Boolean)
        )
    )

    updateAssistantData(projectId, assistant.uid, { allowedDelegationTargetKeys: normalizedTargetKeys }, null)
}

export async function getAssistantDelegationDescriptionStatus(projectId, assistantId) {
    return await runHttpsCallableFunction('getAssistantDelegationDescriptionStatusSecondGen', {
        projectId,
        assistantId,
    })
}

export async function generateAssistantDelegationDescription(projectId, assistantId, language = 'en') {
    return await runHttpsCallableFunction('generateAssistantDelegationDescriptionSecondGen', {
        projectId,
        assistantId,
        language,
    })
}

export function setAssistantLikeDefault(projectId, assistantId) {
    console.log('🔄 setAssistantLikeDefault called:', { projectId, assistantId })

    const { projectAssistants, globalAssistants, loggedUser } = store.getState()

    const batch = new BatchWrapper(getDb())

    // Set the new assistant as default
    updateAssistantData(projectId, assistantId, { isDefault: true }, batch)

    // Find and unmark the current default assistant in the same project
    const assistantsInProject = projectId === GLOBAL_PROJECT_ID ? globalAssistants : projectAssistants[projectId]

    console.log(
        '📋 Assistants in project before update:',
        assistantsInProject?.map(a => ({
            uid: a.uid,
            name: a.displayName,
            isDefault: a.isDefault,
        }))
    )

    if (assistantsInProject) {
        const currentDefaults = assistantsInProject.filter(
            assistant => assistant.isDefault && assistant.uid !== assistantId
        )
        if (currentDefaults.length > 0) {
            console.log(`📌 Found ${currentDefaults.length} current default assistant(s) to unmark`)
            currentDefaults.forEach(currentDefault => {
                console.log('  Unmarking:', {
                    uid: currentDefault.uid,
                    name: currentDefault.displayName,
                })
                updateAssistantData(projectId, currentDefault.uid, { isDefault: false }, batch)
            })
        } else {
            console.log('ℹ️  No existing default assistant found in project')
        }
    }

    // Also set this assistant as the project assistant
    if (projectId !== GLOBAL_PROJECT_ID) {
        const project = ProjectHelper.getProjectById(projectId)
        if (project?.assistantId !== assistantId) {
            console.log('🔗 Also setting as project assistant:', { projectId, assistantId })
            batch.update(getDb().doc(`projects/${projectId}`), { assistantId })
        } else {
            console.log('ℹ️  Assistant is already the project assistant, skipping redundant update')
        }
    }

    batch.commit()

    // Immediately update the Redux store to reflect the change
    const updatedAssistants = assistantsInProject.map(assistant => {
        if (assistant.uid === assistantId) {
            return { ...assistant, isDefault: true }
        } else if (assistant.isDefault) {
            return { ...assistant, isDefault: false }
        }
        return assistant
    })

    console.log(
        '✅ Updated assistants for Redux store:',
        updatedAssistants.map(a => ({
            uid: a.uid,
            name: a.displayName,
            isDefault: a.isDefault,
        }))
    )

    console.log("🎯 Is this the user's default project?", {
        projectId,
        defaultProjectId: loggedUser?.defaultProjectId,
        isDefaultProject: loggedUser?.defaultProjectId === projectId,
    })

    if (projectId === GLOBAL_PROJECT_ID) {
        store.dispatch(setGlobalAssistants(updatedAssistants))
        console.log('🌐 Dispatched setGlobalAssistants')
    } else {
        store.dispatch(setAssistantsInProject(projectId, updatedAssistants))
        console.log('📦 Dispatched setAssistantsInProject for project:', projectId)
    }
}

export function updateAssistantModel(projectId, assistant, model) {
    updateAssistantData(projectId, assistant.uid, { model }, null)
    if (!isGlobalAssistant(assistant.uid))
        assistantModelChangedUpdatesChain(projectId, assistant, assistant.model, model)
}

export function updateAssistantReasoningEffort(projectId, assistant, reasoningEffort) {
    const normalizedEffort = normalizeAssistantReasoningEffort(reasoningEffort)
    updateAssistantData(projectId, assistant.uid, { reasoningEffort: normalizedEffort }, null)
}

export function updateAssistantTemperature(projectId, assistant, temperature) {
    updateAssistantData(projectId, assistant.uid, { temperature }, null)
    if (!isGlobalAssistant(assistant.uid))
        assistantTemperatureChangedUpdatesChain(projectId, assistant, assistant.temperature, temperature)
}

export function updateAssistantRealtimeVoice(projectId, assistant, realtimeVoice) {
    updateAssistantData(projectId, assistant.uid, { realtimeVoice }, null)
}

export function updateAssistantEnabledSkills(projectId, assistant, enabledSkillIds) {
    updateAssistantData(
        projectId,
        assistant.uid,
        { enabledSkillIds: Array.isArray(enabledSkillIds) ? enabledSkillIds : [] },
        null
    )
}

export function updateAssistantEmailSignature(projectId, assistant, emailSignature) {
    updateAssistantData(
        projectId,
        assistant.uid,
        { emailSignature: typeof emailSignature === 'string' ? emailSignature : '' },
        null
    )
}

export function updateAssistantEmailModel(projectId, assistant, emailModel) {
    const normalizedModel = typeof emailModel === 'string' ? emailModel.trim() : ''
    updateAssistantData(
        projectId,
        assistant.uid,
        {
            emailModel: normalizedModel || firebase.firestore.FieldValue.delete(),
        },
        null
    )
}

export function updateAssistantLastVisitedBoardDate(
    projectId,
    assistantId,
    lastVisitBoardProjectId,
    lastVisitBoardProperty
) {
    const { loggedUser } = store.getState()
    updateAssistantData(
        projectId,
        assistantId,
        {
            [`${lastVisitBoardProperty}.${lastVisitBoardProjectId}.${loggedUser.uid}`]: Date.now(),
        },
        null
    )
}

export async function updateAssistantAvatar(projectId, assistant, pictureFile) {
    return runWithLoading('update_assistant_avatar', async () => {
        const pictures = await proccessPictureForAvatar(pictureFile)

        if (pictures.length > 0) {
            await deleteFolderFilesInStorage(`assistants/${projectId}/items/${assistant.uid}`)
            const urlList = await uploadAvatarPhotos(
                pictures,
                `assistants/${projectId}/${assistant.uid}/${assistant.uid}@${Date.now()}`,
                ''
            )

            const updatedData = {
                photoURL: urlList[0],
                photoURL50: urlList[1],
                photoURL300: urlList[2],
            }

            await updateAssistantData(projectId, assistant.uid, updatedData, null)

            if (!isGlobalAssistant(assistant.uid))
                assistantPictureChangedUpdatesChain(projectId, assistant, updatedData.photoURL50)
        }
    })
}

export async function discoverExternalToolsForIframeLink(link) {
    const sourceUrl = typeof link === 'string' ? link.trim() : ''
    if (!sourceUrl) {
        return { success: false, error: 'Missing iframe URL' }
    }

    try {
        return await runHttpsCallableFunction('discoverExternalToolsSecondGen', { url: sourceUrl }, { timeout: 20000 })
    } catch (error) {
        console.error('Error discovering external tools for iframe link:', error)
        return {
            success: false,
            sourceUrl,
            error: error.message || 'Failed to discover external tools',
        }
    }
}

export function uploadNewPreConfigTask(projectId, assistantId, task) {
    clearAssistantTasksCache(projectId, assistantId)
    const taskId = getId()
    task.id = taskId

    const taskToStore = { ...task }
    delete taskToStore.id
    delete taskToStore.aiModel
    delete taskToStore.aiTemperature
    if (!getPreConfigTaskModelOverride(taskToStore)) delete taskToStore.aiModelOverride
    if (taskToStore.aiReasoningEffort === undefined) delete taskToStore.aiReasoningEffort
    taskToStore.assistantId = assistantId

    const collectionPath = getAssistantTasksCollectionPath(projectId, assistantId)
    const collectionRef = getDb().collection(collectionPath)

    const query = isGlobalAssistant(assistantId) ? collectionRef.where('assistantId', '==', assistantId) : collectionRef

    query
        .get()
        .then(snapshot => {
            taskToStore.order = snapshot.size

            if (!taskToStore.creatorUserId) {
                const { loggedUser } = store.getState()
                if (loggedUser?.uid) {
                    taskToStore.creatorUserId = loggedUser.uid
                    taskToStore.activatorUserId = loggedUser.uid
                }
            }

            if (!isGlobalAssistant(assistantId)) {
                taskToStore.activatedInProjectId = projectId
                taskToStore.lastExecuted = null
            }

            const batch = new BatchWrapper(getDb())
            updateAssistantData(projectId, assistantId, {}, batch)
            batch.set(getAssistantTaskDocRef(projectId, assistantId, taskId), {
                ...taskToStore,
                id: taskId,
            })

            if (!isGlobalAssistant(assistantId)) {
                const legacyRef = getDb().doc(`assistantTasks/${projectId}/preConfigTasks/${taskId}`)
                batch.delete(legacyRef)
            }

            batch.commit()
        })
        .catch(error => {
            console.error('Error getting task count for order:', error)
            taskToStore.order = 999

            if (!taskToStore.creatorUserId) {
                const { loggedUser } = store.getState()
                if (loggedUser?.uid) {
                    taskToStore.creatorUserId = loggedUser.uid
                    taskToStore.activatorUserId = loggedUser.uid
                }
            }

            if (!isGlobalAssistant(assistantId)) {
                taskToStore.activatedInProjectId = projectId
                taskToStore.lastExecuted = null
            }

            const batch = new BatchWrapper(getDb())
            updateAssistantData(projectId, assistantId, {}, batch)
            batch.set(getAssistantTaskDocRef(projectId, assistantId, taskId), {
                ...taskToStore,
                id: taskId,
            })

            if (!isGlobalAssistant(assistantId)) {
                const legacyRef = getDb().doc(`assistantTasks/${projectId}/preConfigTasks/${taskId}`)
                batch.delete(legacyRef)
            }

            batch.commit()
        })
}

export function updatePreConfigTask(projectId, assistantId, task) {
    clearAssistantTasksCache(projectId, assistantId)
    const taskToStore = { ...task }
    delete taskToStore.id
    const aiModelOverride = getPreConfigTaskModelOverride(taskToStore)
    delete taskToStore.aiModel
    delete taskToStore.aiTemperature
    if (!aiModelOverride) delete taskToStore.aiModelOverride
    const hasReasoningEffortOverride = taskToStore.aiReasoningEffort !== undefined
    if (!hasReasoningEffortOverride) delete taskToStore.aiReasoningEffort
    taskToStore.assistantId = assistantId

    // Get the current task data to compare changes
    const taskRef = getAssistantTaskDocRef(projectId, assistantId, task.id)

    taskRef.get().then(doc => {
        const currentTask = doc.exists ? doc.data() : null

        // Check if start time or date has changed
        if (currentTask && (currentTask.startDate !== task.startDate || currentTask.startTime !== task.startTime)) {
            // Clear lastExecuted if timing has changed
            taskToStore.lastExecuted = null
            console.log('Clearing lastExecuted due to timing change:', {
                taskId: task.id,
                oldStartDate: currentTask.startDate,
                newStartDate: task.startDate,
                oldStartTime: currentTask.startTime,
                newStartTime: task.startTime,
            })
        }

        const payload = {
            ...taskToStore,
            id: task.id,
            aiModel: firebase.firestore.FieldValue.delete(),
            aiTemperature: firebase.firestore.FieldValue.delete(),
            ...(!aiModelOverride ? { aiModelOverride: firebase.firestore.FieldValue.delete() } : { aiModelOverride }),
            ...(!hasReasoningEffortOverride
                ? { aiReasoningEffort: firebase.firestore.FieldValue.delete() }
                : { aiReasoningEffort: taskToStore.aiReasoningEffort }),
        }

        if (!payload.creatorUserId) {
            payload.creatorUserId = currentTask?.creatorUserId || task?.creatorUserId
        }
        if (!payload.activatorUserId) {
            payload.activatorUserId = currentTask?.activatorUserId || task?.activatorUserId || payload.creatorUserId
        }

        const activatedUserIds = getActivatedUserIds({
            ...currentTask,
            ...task,
            creatorUserId: payload.creatorUserId,
            activatorUserId: payload.activatorUserId,
        })
        if (activatedUserIds.length > 0) payload.activatedUserIds = activatedUserIds

        const batch = new BatchWrapper(getDb())
        updateAssistantData(projectId, assistantId, {}, batch)
        if (doc.exists) {
            batch.update(taskRef, payload)
        } else {
            batch.set(taskRef, payload, { merge: true })
        }

        if (!isGlobalAssistant(assistantId)) {
            const legacyRef = getDb().doc(`assistantTasks/${projectId}/preConfigTasks/${task.id}`)
            batch.delete(legacyRef)
        }
        batch.commit()
    })
}

export async function toggleRecurringTaskActivation(
    projectId,
    assistantId,
    taskId,
    recurrenceValue = null,
    activatedInProjectId = projectId
) {
    clearAssistantTasksCache(projectId, assistantId)
    const { loggedUser } = store.getState()
    if (!loggedUser?.uid || !taskId) return

    const taskRef = getAssistantTaskDocRef(projectId, assistantId, taskId)

    await getDb().runTransaction(async transaction => {
        const doc = await transaction.get(taskRef)
        if (!doc.exists) return

        const task = doc.data()
        if (!task?.recurrence || task.recurrence === RECURRENCE_NEVER) return

        const currentUserId = loggedUser.uid
        const activatedUserIds = getActivatedUserIds(task)
        const isActivatedForCurrentUser = activatedUserIds.includes(currentUserId)
        const nextActivatedUserIds = isActivatedForCurrentUser
            ? activatedUserIds.filter(userId => userId !== currentUserId)
            : [...activatedUserIds, currentUserId]

        const updatePayload = {
            activatedUserIds: nextActivatedUserIds,
            activatorUserId: currentUserId,
        }
        const nextRecurrenceByUser =
            task.recurrenceByUser && typeof task.recurrenceByUser === 'object' ? { ...task.recurrenceByUser } : {}

        if (!task.creatorUserId) {
            updatePayload.creatorUserId = currentUserId
        }

        updatePayload[`activatedInProjectIdByUser.${currentUserId}`] = activatedInProjectId
        if (!isGlobalAssistant(assistantId)) updatePayload.activatedInProjectId = activatedInProjectId

        const perUserLastExecutedField = `lastExecutedByUser.${currentUserId}`
        if (isActivatedForCurrentUser) {
            updatePayload[perUserLastExecutedField] = firebase.firestore.FieldValue.delete()
            delete nextRecurrenceByUser[currentUserId]
        } else {
            const recurrenceToActivate =
                recurrenceValue || task.recurrenceByUser?.[currentUserId] || task.recurrence || RECURRENCE_DAILY
            if (task.lastExecutedByUser?.[currentUserId] === undefined || recurrenceToActivate === RECURRENCE_ONCE) {
                updatePayload[perUserLastExecutedField] = null
            }
            nextRecurrenceByUser[currentUserId] = recurrenceToActivate
            if (recurrenceToActivate === RECURRENCE_ONCE) {
                updatePayload.completedOneOffUserIds = firebase.firestore.FieldValue.arrayRemove(currentUserId)
            }
        }

        updatePayload.recurrenceByUser = nextRecurrenceByUser
        transaction.update(taskRef, updatePayload)
    })
}

export function deletePreConfigTask(projectId, assistantId, taskId) {
    clearAssistantTasksCache(projectId, assistantId)
    const batch = new BatchWrapper(getDb())
    updateAssistantData(projectId, assistantId, {}, batch)
    batch.delete(getAssistantTaskDocRef(projectId, assistantId, taskId))

    if (!isGlobalAssistant(assistantId)) {
        const legacyRef = getDb().doc(`assistantTasks/${projectId}/preConfigTasks/${taskId}`)
        batch.delete(legacyRef)
    }
    batch.commit()
}

export function updateAssistantTasksOrder(projectId, assistantId, tasks) {
    clearAssistantTasksCache(projectId, assistantId)
    // Create a batch operation
    const batch = new BatchWrapper(getDb())

    try {
        // Update the assistant's last edit data
        updateAssistantData(projectId, assistantId, {}, batch)

        // Update each task's order field
        tasks.forEach((task, index) => {
            if (!task || !task.id) {
                console.error('Invalid task found during reordering:', task)
                return
            }

            const taskRef = getAssistantTaskDocRef(projectId, assistantId, task.id)
            batch.set(taskRef, { order: index }, { merge: true })
            if (!isGlobalAssistant(assistantId)) {
                const legacyRef = getDb().doc(`assistantTasks/${projectId}/preConfigTasks/${task.id}`)
                batch.delete(legacyRef)
            }
        })

        // Commit the batch
        batch.commit()
    } catch (error) {
        console.error('Error updating task order:', error)
    }
}

export const updateAssistantLastCommentData = async (projectId, assistantId, lastComment, lastCommentType) => {
    getDb()
        .doc(`assistants/${projectId}/items/${assistantId}`)
        .update({
            [`commentsData.lastComment`]: lastComment,
            [`commentsData.lastCommentType`]: lastCommentType,
            [`commentsData.amount`]: firebase.firestore.FieldValue.increment(1),
        })
}

export const resetAssistantLastCommentData = async (projectId, assistantId) => {
    const ref = getDb().doc(`assistants/${projectId}/items/${assistantId}`)
    const doc = await ref.get()
    if (doc.exists) {
        const data = doc.data()
        if (data.commentsData && data.commentsData.amount > 0) {
            ref.update({
                [`commentsData.lastComment`]: null,
                [`commentsData.lastCommentType`]: null,
                [`commentsData.amount`]: 0,
            })
        }
    }
}

export function setAssistantLastVisitedBoardDate(
    projectId,
    assistant,
    lastVisitBoardProjectId,
    lastVisitBoardProperty
) {
    const { projectAssistants, loggedUser, globalAssistants } = store.getState()

    const updatedAssistant = {
        ...assistant,
        [lastVisitBoardProperty]: {
            ...assistant?.[lastVisitBoardProperty],
            [lastVisitBoardProjectId]: {
                ...assistant?.[lastVisitBoardProperty]?.[lastVisitBoardProjectId],
                [loggedUser.uid]: Date.now(),
            },
        },
    }

    if (projectId === GLOBAL_PROJECT_ID) {
        const newGlobalAssistants = [
            updatedAssistant,
            ...globalAssistants.filter(item => item.uid !== updatedAssistant.uid),
        ]
        store.dispatch(setGlobalAssistants(newGlobalAssistants))
    } else {
        const index = projectAssistants[projectId].findIndex(assistantItem => assistantItem.uid === assistant.uid)
        projectAssistants[projectId][index] = updatedAssistant

        store.dispatch(
            setAssistantsInProject(projectId, [
                updatedAssistant,
                ...projectAssistants[projectId].filter(item => item.uid !== updatedAssistant.uid),
            ])
        )
    }

    updateAssistantLastVisitedBoardDate(projectId, assistant.uid, lastVisitBoardProjectId, lastVisitBoardProperty)
}

//OTHERS FUNCTIONS

export async function addGlobalAssistantToProject(projectId, assistantId) {
    await getDb()
        .doc(`projects/${projectId}`)
        .update({ globalAssistantIds: firebase.firestore.FieldValue.arrayUnion(assistantId) })
}

export async function moveAssistantToProject(sourceProjectId, targetProjectId, assistantId) {
    if (!sourceProjectId || !targetProjectId || !assistantId || sourceProjectId === targetProjectId) return false

    const sourceAssistantRef = getDb().doc(`assistants/${sourceProjectId}/items/${assistantId}`)
    const targetAssistantRef = getDb().doc(`assistants/${targetProjectId}/items/${assistantId}`)

    const [sourceAssistantDoc, sourceProjectDoc, targetAssistantsSnapshot, sourceTasksSnapshot] = await Promise.all([
        sourceAssistantRef.get(),
        getDb().doc(`projects/${sourceProjectId}`).get(),
        getDb().collection(`assistants/${targetProjectId}/items`).where('isDefault', '==', true).get(),
        getDb().collection(getAssistantTasksCollectionPath(sourceProjectId, assistantId)).get(),
    ])

    if (!sourceAssistantDoc.exists) return false

    const sourceAssistantData = sourceAssistantDoc.data()
    const batch = new BatchWrapper(getDb())

    batch.set(
        targetAssistantRef,
        {
            ...sourceAssistantData,
            isDefault: true,
        },
        { merge: true }
    )

    sourceTasksSnapshot.forEach(doc => {
        const task = doc.data()
        const movedTask = {
            ...task,
            assistantId,
        }

        if (movedTask.activatedInProjectId) {
            movedTask.activatedInProjectId = targetProjectId
        }

        batch.set(getAssistantTaskDocRef(targetProjectId, assistantId, doc.id), movedTask, { merge: true })
        batch.delete(doc.ref)
    })

    targetAssistantsSnapshot.forEach(doc => {
        if (doc.id !== assistantId) {
            batch.update(doc.ref, { isDefault: false })
        }
    })

    if (sourceProjectDoc.exists && sourceProjectDoc.data()?.assistantId === assistantId) {
        batch.update(getDb().doc(`projects/${sourceProjectId}`), { assistantId: '' })
    }

    batch.update(getDb().doc(`projects/${targetProjectId}`), { assistantId })
    batch.delete(sourceAssistantRef)

    await batch.commit()
    return true
}

export function removeGlobalAssistantFromProject(projectId, assistantId) {
    getDb()
        .doc(`projects/${projectId}`)
        .update({ globalAssistantIds: firebase.firestore.FieldValue.arrayRemove(assistantId) })
}

export async function copyPreConfigTasksToNewAssistant(
    sourceProjectId,
    sourceAssistantId,
    targetProjectId,
    targetAssistantId
) {
    try {
        const { loggedUser } = store.getState()
        const currentUserId = loggedUser?.uid || null

        console.log('copyPreConfigTasksToNewAssistant called with:', {
            sourceProjectId,
            sourceAssistantId,
            targetProjectId,
            targetAssistantId,
        })

        // Get all pre-configured tasks from the source assistant
        const tasksSnapshot = await getDb()
            .collection(`assistantTasks/${sourceProjectId}/preConfigTasks`)
            .where('assistantId', '==', sourceAssistantId)
            .orderBy('order', 'asc')
            .get()

        console.log(`Found ${tasksSnapshot.size} tasks to copy`)

        if (tasksSnapshot.empty) {
            console.log('No pre-configured tasks to copy')
            return
        }

        const batch = new BatchWrapper(getDb())

        // Copy each task to the new assistant
        tasksSnapshot.forEach(doc => {
            const task = doc.data()
            const newTaskId = getId()

            const taskCopy = {
                ...getTaskTemplateState(task),
                assistantId: targetAssistantId,
                id: newTaskId,
                // Track source template task for update sync
                copiedFromTemplateTaskId: doc.id,
                copiedFromTemplateTaskDate: Date.now(),
                templateTaskSnapshot: getTaskTemplateState(task),
                templateTaskSyncConflicts: [],
                templateSyncStatus: 'synced',
            }
            delete taskCopy.lastExecutedByUser
            delete taskCopy.recurrenceByUser
            delete taskCopy.activatedUserIds

            const targetIsGlobal = isGlobalAssistant(targetAssistantId)

            if (!targetIsGlobal) {
                taskCopy.activatedInProjectId = targetProjectId
                taskCopy.lastExecuted = null
                taskCopy.lastExecutedByUser = {}

                if (currentUserId) {
                    taskCopy.creatorUserId = currentUserId
                    taskCopy.activatorUserId = currentUserId
                } else {
                    delete taskCopy.creatorUserId
                    delete taskCopy.activatorUserId
                }

                if (taskCopy.recurrence && taskCopy.recurrence !== RECURRENCE_NEVER && currentUserId) {
                    taskCopy.recurrenceByUser = { [currentUserId]: taskCopy.recurrence }
                    taskCopy.activatedUserIds = [currentUserId]
                } else {
                    taskCopy.recurrenceByUser = {}
                    taskCopy.activatedUserIds = []
                }
            }

            console.log('Copying task:', task.title || task.name, 'with new ID:', newTaskId)

            batch.set(getAssistantTaskDocRef(targetProjectId, targetAssistantId, newTaskId), taskCopy)

            if (!targetIsGlobal) {
                const legacyRef = getDb().doc(`assistantTasks/${targetProjectId}/preConfigTasks/${newTaskId}`)
                batch.delete(legacyRef)
            }
        })

        await batch.commit()
        console.log(`✅ Successfully copied ${tasksSnapshot.size} pre-configured tasks to new assistant`)
    } catch (error) {
        console.error('❌ Error copying pre-configured tasks:', error)
        console.error('Error details:', {
            message: error.message,
            stack: error.stack,
        })
    }
}

export async function resolveAssistantTemplateConflicts(projectId, assistantId, acceptedFields, resolvedFields) {
    return runHttpsCallableFunction('acceptAssistantTemplateConflictsSecondGen', {
        projectId,
        assistantId,
        acceptedFields,
        resolvedFields,
    })
}

// Update assistant properties from a global/template assistant
export async function updateAssistantFromTemplate(projectId, localAssistant, globalAssistant) {
    const templateHasEmailModel = Object.prototype.hasOwnProperty.call(globalAssistant, 'emailModel')
    const updatePayload = {
        displayName: globalAssistant.displayName,
        description: globalAssistant.description,
        photoURL: globalAssistant.photoURL,
        photoURL50: globalAssistant.photoURL50,
        photoURL300: globalAssistant.photoURL300,
        instructions: globalAssistant.instructions,
        emailSignature:
            typeof globalAssistant.emailSignature === 'string'
                ? globalAssistant.emailSignature
                : DEFAULT_EMAIL_SIGNATURE,
        model: globalAssistant.model,
        emailModel: templateHasEmailModel ? globalAssistant.emailModel : firebase.firestore.FieldValue.delete(),
        heartbeatModel: globalAssistant.heartbeatModel ?? globalAssistant.model,
        reasoningEffort: normalizeAssistantReasoningEffort(globalAssistant.reasoningEffort),
        ...(Object.prototype.hasOwnProperty.call(globalAssistant, 'heartbeatReasoningEffort')
            ? {
                  heartbeatReasoningEffort: normalizeAssistantReasoningEffort(globalAssistant.heartbeatReasoningEffort),
              }
            : {}),
        temperature: globalAssistant.temperature,
        allowedTools: Array.isArray(globalAssistant.allowedTools) ? globalAssistant.allowedTools : [],
        enabledSkillIds: Array.isArray(globalAssistant.enabledSkillIds) ? globalAssistant.enabledSkillIds : [],
        delegationToolDescriptionManual: globalAssistant.delegationToolDescriptionManual || '',
        delegationToolDescriptionGenerated: globalAssistant.delegationToolDescriptionGenerated || '',
        delegationToolDescriptionGeneratedAt: globalAssistant.delegationToolDescriptionGeneratedAt || null,
        delegationToolDescriptionInputHash: globalAssistant.delegationToolDescriptionInputHash || '',
        prompt: globalAssistant.prompt,
        thirdPartLink: globalAssistant.thirdPartLink,
        type: globalAssistant.type,
        heartbeatAwakeStart: globalAssistant.heartbeatAwakeStart ?? DEFAULT_HEARTBEAT_AWAKE_START,
        heartbeatAwakeEnd: globalAssistant.heartbeatAwakeEnd ?? DEFAULT_HEARTBEAT_AWAKE_END,
        heartbeatIntervalMs: globalAssistant.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_INTERVAL_MS,
        heartbeatChancePercent: globalAssistant.heartbeatChancePercent ?? DEFAULT_HEARTBEAT_CHANCE_PERCENT,
        heartbeatChanceNoReplyPercent:
            globalAssistant.heartbeatChanceNoReplyPercent ?? DEFAULT_HEARTBEAT_CHANCE_NO_REPLY_PERCENT,
        heartbeatSendWhatsApp: globalAssistant.heartbeatSendWhatsApp ?? DEFAULT_HEARTBEAT_SEND_WHATSAPP,
        heartbeatPrompt:
            typeof globalAssistant.heartbeatPrompt === 'string'
                ? globalAssistant.heartbeatPrompt
                : DEFAULT_HEARTBEAT_PROMPT,
        // Reset sync timestamp to indicate we've synced with the latest version
        copiedFromTemplateAssistantDate: Date.now(),
    }

    await updateAssistantData(projectId, localAssistant.uid, updatePayload, null)
    console.log(`✅ Updated assistant ${localAssistant.uid} from template ${globalAssistant.uid}`)
    const updatedAssistant = { ...localAssistant, ...updatePayload }
    if (!templateHasEmailModel) delete updatedAssistant.emailModel
    return updatedAssistant
}

// Sync pre-configured tasks from a global/template assistant
export async function syncPreConfigTasksFromTemplate(globalAssistantId, localProjectId, localAssistantId) {
    try {
        const { loggedUser } = store.getState()
        const currentUserId = loggedUser?.uid || null
        const syncTimestamp = Date.now()

        const normalizeOrder = value => (typeof value === 'number' && Number.isFinite(value) ? value : 9999)

        // 1. Get global/template tasks
        const globalTasksSnapshot = await getDb()
            .collection(getAssistantTasksCollectionPath(GLOBAL_PROJECT_ID, globalAssistantId))
            .where('assistantId', '==', globalAssistantId)
            .get()

        // 2. Get local tasks
        const localTasksSnapshot = await getDb()
            .collection(getAssistantTasksCollectionPath(localProjectId, localAssistantId))
            .get()

        const globalTasks = []
        globalTasksSnapshot.forEach(doc => {
            globalTasks.push({ ...doc.data(), id: doc.id })
        })
        globalTasks.sort((a, b) => normalizeOrder(a.order) - normalizeOrder(b.order))

        // 3. Build mapping: copiedFromTemplateTaskId -> local task
        const localTasksByTemplateId = {}
        const localManualTasks = []
        localTasksSnapshot.forEach(doc => {
            const task = doc.data()
            if (task.copiedFromTemplateTaskId) {
                localTasksByTemplateId[task.copiedFromTemplateTaskId] = { ...task, id: doc.id }
            } else {
                localManualTasks.push({ ...task, id: doc.id })
            }
        })
        localManualTasks.sort((a, b) => normalizeOrder(a.order) - normalizeOrder(b.order))

        const batch = new BatchWrapper(getDb())
        const templateTasksCount = globalTasks.length

        // 4. For each global task, update or create local task
        globalTasks.forEach((globalTask, index) => {
            const localTask = localTasksByTemplateId[globalTask.id]
            const templateOrder = index
            const templatePayload = { ...getTaskTemplateState(globalTask), order: templateOrder }

            if (localTask) {
                const shouldPreserveLocalRecurringTime =
                    localTask.recurrence && localTask.recurrence !== RECURRENCE_NEVER

                // Update existing task - preserve local-only fields (lastExecuted, activatedInProjectId, etc.)
                const updatePayload = {
                    ...templatePayload,
                    assistantId: localAssistantId,
                    copiedFromTemplateTaskId: globalTask.id,
                    order: templateOrder,
                    // Update sync timestamp
                    copiedFromTemplateTaskDate: syncTimestamp,
                    templateTaskSnapshot: templatePayload,
                    templateTaskSyncConflicts: [],
                    templateSyncStatus: 'synced',
                }

                // Preserve project-level recurring schedule time customizations.
                if (shouldPreserveLocalRecurringTime) {
                    if (localTask.startDate !== undefined && localTask.startDate !== null) {
                        updatePayload.startDate = localTask.startDate
                    }
                    if (localTask.startTime !== undefined && localTask.startTime !== null) {
                        updatePayload.startTime = localTask.startTime
                    }
                    if (localTask.userTimezone !== undefined && localTask.userTimezone !== null) {
                        updatePayload.userTimezone = localTask.userTimezone
                    }
                }

                batch.update(getAssistantTaskDocRef(localProjectId, localAssistantId, localTask.id), updatePayload)
            } else {
                // Create new local task
                const newTaskId = getId()
                const taskCopy = {
                    ...templatePayload,
                    id: newTaskId,
                    assistantId: localAssistantId,
                    copiedFromTemplateTaskId: globalTask.id,
                    copiedFromTemplateTaskDate: syncTimestamp,
                    order: templateOrder,
                    templateTaskSnapshot: templatePayload,
                    templateTaskSyncConflicts: [],
                    templateSyncStatus: 'synced',
                    activatedInProjectId: localProjectId,
                    lastExecuted: null,
                    lastExecutedByUser: {},
                }
                if (currentUserId) {
                    taskCopy.creatorUserId = currentUserId
                    taskCopy.activatorUserId = currentUserId
                } else {
                    delete taskCopy.creatorUserId
                    delete taskCopy.activatorUserId
                }

                if (taskCopy.recurrence && taskCopy.recurrence !== RECURRENCE_NEVER && currentUserId) {
                    taskCopy.recurrenceByUser = { [currentUserId]: taskCopy.recurrence }
                    taskCopy.activatedUserIds = [currentUserId]
                } else {
                    taskCopy.recurrenceByUser = {}
                    taskCopy.activatedUserIds = []
                }

                batch.set(getAssistantTaskDocRef(localProjectId, localAssistantId, newTaskId), taskCopy)

                // Clean up legacy path
                const legacyRef = getDb().doc(`assistantTasks/${localProjectId}/preConfigTasks/${newTaskId}`)
                batch.delete(legacyRef)
            }
        })

        // Keep manual tasks and ensure they don't collide with template task order.
        localManualTasks.forEach((task, index) => {
            const desiredOrder = templateTasksCount + index
            if (task.order !== desiredOrder) {
                batch.set(
                    getAssistantTaskDocRef(localProjectId, localAssistantId, task.id),
                    { order: desiredOrder },
                    { merge: true }
                )
            }
        })

        await batch.commit()
        console.log(`✅ Synced ${globalTasks.length} tasks from template to assistant ${localAssistantId}`)
    } catch (error) {
        console.error('❌ Error syncing pre-configured tasks from template:', error)
        console.error('Error details:', {
            message: error.message,
            stack: error.stack,
        })
    }
}
